import React, { useEffect } from 'react';

// Remove injected badge
(function removeBadge() {
  const remove = () => { const b = document.getElementById('emergent-badge'); if (b) b.remove(); };
  remove();
  setInterval(remove, 100);
})();

import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from './components/ui/sonner';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { MusicPlayerProvider } from './context/MusicPlayerContext';
import { VideoCallProvider } from './context/VideoCallContext';
import { NotificationProvider, useNotifications } from './context/NotificationContext';
import { TwilioNotificationProvider, useTwilioNotifications } from './context/TwilioNotificationContext';
import { TwilioVoiceProvider } from './context/TwilioVoiceContext';
import { PageHeaderProvider } from './context/PageHeaderContext';

// Pages
import AuthPage from './pages/AuthPage';
import HomePage from './pages/HomePage';
import CallsPage from './pages/CallsPage';
import HistoryPage from './pages/HistoryPage';
import ChatPage from './pages/ChatPage';
import CalendarPage from './pages/CalendarPage';
import TasksPage from './pages/TasksPage';
import ResourcesPage from './pages/ResourcesPage';
import EmailPage from './pages/EmailPage';
import SettingsPage from './pages/SettingsPage';
import ProfilePage from './pages/ProfilePage';
import UserSearchPage from './pages/UserSearchPage';
import IdentityVerificationPage from './pages/IdentityVerificationPage';
import RecoveryKeyApprovalsPage from './pages/RecoveryKeyApprovalsPage';
import MFAPage from './pages/MFAPage';
import NDAPage from './pages/NDAPage';
import LeadershipInterviewPage from './pages/LeadershipInterviewPage';
import SubmitApplicationPage from './pages/SubmitApplicationPage';
import MyTeamPage from './pages/MyTeamPage';
import MyPeersPage from './pages/MyPeersPage';
import HowOrbWorksPage from './pages/HowOrbWorksPage';
import AgreementSigningPage from './pages/AgreementSigningPage';
import CharacterProfilePage from './pages/CharacterProfilePage';
import CharacterStoryPage from './pages/CharacterStoryPage';
import NotificationsPage from './pages/NotificationsPage';
import MasterContactsPage from './pages/MasterContactsPage';
import AdminCallingPage from './pages/AdminCallingPage';
import StatePhoneNumbersPage from './pages/StatePhoneNumbersPage';
import StatewideDetailsPage from './pages/StatewideDetailsPage';
// iter153 — RestrictedSignupPage kept imported (currently unused) so
// future re-enablement is a one-line swap in the route definition.
// eslint-disable-next-line no-unused-vars
import RestrictedSignupPage from './pages/RestrictedSignupPage';
import RestrictedSignupClosed from './pages/RestrictedSignupClosed';
import OrbUniverseSigninPage from './pages/OrbUniverseSigninPage';
import AdminRescuePage from './pages/AdminRescuePage';
import AdminOrbUniversesPage from './pages/AdminOrbUniversesPage';
import CustomizationPage from './pages/CustomizationPage';
import OnboardingCustomizationPage from './pages/OnboardingCustomizationPage';
import { StateScopeProvider } from './context/StateScopeContext';
import RestrictedStatesPage from './pages/RestrictedStatesPage';
import MasterHistoryPage from './pages/MasterHistoryPage';
import PhoneRoutingPage from './pages/PhoneRoutingPage';
import AssignmentsPage from './pages/AssignmentsPage';
import VoiceAgentTrainingPage from './pages/VoiceAgentTrainingPage';
import VoiceAgentDashboardPage from './pages/VoiceAgentDashboardPage';
import VoicemailPage from './pages/VoicemailPage';
import MemosPage from './pages/MemosPage';
import AdminMemosPage from './pages/AdminMemosPage';
import AdminMemoContactDetailPage from './pages/AdminMemoContactDetailPage';
import AdminMemoTeamDetailPage from './pages/AdminMemoTeamDetailPage';
import UserCommunicationsPage from './pages/UserCommunicationsPage';
import OrbCharactersPage from './pages/OrbCharactersPage';
import MessagesPage from './pages/MessagesPage';
import UserAdminPage from './pages/UserAdminPage';
import SecurityRequirementsPage from './pages/SecurityRequirementsPage';
import AdminLlmSlotsPage from './pages/AdminLlmSlotsPage';
import HelpButtonsPage from './pages/HelpButtonsPage';
import ContactsPage from './pages/ContactsPage';
import UnassignedCallersPage from './pages/UnassignedCallersPage';
import BusinessCardPage from './pages/BusinessCardPage';
import ViewBusinessCardPage from './pages/ViewBusinessCardPage';
import LinkPhysicalCardPage from './pages/LinkPhysicalCardPage';
import PublicBusinessCardPage from './pages/PublicBusinessCardPage';
import SigCallPage from './pages/SigCallPage';
import SigEmailPage from './pages/SigEmailPage';
import AppStoreRedirectPage from './pages/AppStoreRedirectPage';
import OnboardingAdminPage from './pages/OnboardingAdminPage';
import EmailVerificationsAdminPage from './pages/EmailVerificationsAdminPage';
import AdminOrbEmailsPage from './pages/AdminOrbEmailsPage';
import AITrainingHubPage from './pages/AITrainingHubPage';
import CharacterTrainingPage from './pages/CharacterTrainingPage';
import BusinessCardManagementPage from './pages/BusinessCardManagementPage';
import FeedbackPage from './pages/FeedbackPage';
import FeedbackAdminPage from './pages/FeedbackAdminPage';

// Components
import Layout from './components/Layout';
import MiniPlayer from './components/MiniPlayer';
import PostCallReassignPrompt from './components/PostCallReassignPrompt';
import { IncomingCallModal, FloatingCallWindow, VideoCallContainer } from './components/video-call';
import IncomingPSTNCallModal from './components/IncomingPSTNCallModal';
import IncomingCallConnectingOverlay from './components/IncomingCallConnectingOverlay';
import ActiveExternalCall from './components/ActiveExternalCall';
import IOSInstallPrompt from './components/IOSInstallPrompt';
import NotificationPermissionPrompt from './components/NotificationPermissionPrompt';

// Scroll to top on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  
  return null;
};

// Protected Route wrapper
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse flex flex-col items-center gap-4">
          <div className="w-16 h-16 bg-primary/20 rounded-2xl" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return <Layout>{children}</Layout>;
};


// ──────── Onboarding gate ────────
// Wraps any route that should be UNREACHABLE until the user has
// `is_onboarded === true`. We render the user's normal Layout chrome
// (so the bottom nav stays visible AND clickable per spec) and then
// show the OnboardingLockedOverlay on top of the page content.
//
// Allowed pages while NOT onboarded:
//   /            — onboarding home
//   /settings/*  — settings
//   /profile     — profile
//   /feedback    — bug report (per user spec)
//   plus every onboarding step page
//     /submit-application, /identity-verification, /nda, /mfa,
//     /leadership-interview, /how-orb-works, /agreement
// ──────── Public-only Route Guard ────────
// Routes like /login or /signup/* — if the user is ALREADY signed in,
// redirect them to the home page so they don't see the signin form.
//   plus public pages /login, /signup/*, /card/*, /u/*
//
// EVERY other route uses <RequireOnboarded> so a non-onboarded user
// either sees the overlay (when they have the unlock-able page) or
// is auto-redirected to / (when the page must not even render its
// shell, e.g. the admin dashboard).
const _OnboardingLockedShell = ({ pageName }) => {
  // Lazy-import to dodge the circular dep AuthContext → App → context.
  const Comp = require('./components/OnboardingLockedOverlay').default;
  return <Comp pageName={pageName} />;
};

const RequireOnboarded = ({ children, pageName, hideForAdmin = true }) => {
  const { user } = useAuth();
  const isOnboarded = !!user?.is_onboarded;
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  if (isOnboarded || (hideForAdmin && isAdmin)) return children;
  return <_OnboardingLockedShell pageName={pageName} />;
};

// Public Route wrapper (redirect if already authenticated)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse flex flex-col items-center gap-4">
          <div className="w-16 h-16 bg-primary/20 rounded-2xl" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }
  
  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }
  
  return children;
};

function AppRoutes() {
  return (
    <>
      <ScrollToTop />
      <Routes>
        {/* Public Routes */}
        {/* iter153 — canonical sign-in path is `/login`. The legacy
            `/auth` URL kept as a redirect so any bookmarks, emailed
            recovery links, or third-party docs that still point at
            `/auth` keep working. New code MUST use `/login`.
            ⚠️  Backend API routes under `/api/auth/...` are SEPARATE
            and unchanged — they live on the backend and are how the
            login form actually talks to the server. */}
        <Route path="/login" element={
          <PublicRoute>
            <AuthPage />
          </PublicRoute>
        } />
        <Route path="/auth" element={<Navigate to="/login" replace />} />
        <Route path="/card/:userId" element={<PublicBusinessCardPage />} />
        <Route path="/sig-call/:token" element={<SigCallPage />} />
        <Route path="/sig-email/:token" element={<SigEmailPage />} />
        {/* v3 (current) — `www.orbuplift.com/in/<decoy>` is the canonical
            public business card URL. LinkedIn-style `/in/` namespace
            keeps the root clean and eliminates slug-collision risk
            with system paths (`/download`, `/login`, etc.). */}
        <Route path="/in/:userId" element={<PublicBusinessCardPage />} />
        {/* `/download` — public App Store / Play Store smart redirect.
            Reached via `www.orbuplift.com/download`. */}
        <Route path="/download" element={<AppStoreRedirectPage />} />
        {/* v2 (legacy backwards-compat) — old printed/NFC cards in
            circulation still pointing at `collectiveorb.app/uplift/<id>`
            keep resolving. The /uplift root (no id) keeps the
            UA-aware App / Play store redirect.

            NOTE on Universal Links: we intentionally do NOT register
            `/in/*` or `/uplift/*` (business card paths) with the
            native app's Universal Links / App Links. Business cards
            must remain a pure web experience for outsiders — they
            should NEVER trigger an "open in app" hand-off. Only
            `/app/*` (internal team navigation) and `/download` are
            candidates for native deep-linking. See `.well-known/`
            handlers if/when those get scoped. */}
        <Route path="/uplift" element={<AppStoreRedirectPage />} />
        <Route path="/uplift/:userId" element={<PublicBusinessCardPage />} />
        {/* iter153 — restricted-state public signup is intentionally
            DISABLED until further notice. ORB-sourced users come in
            via /u/:universe_id/signin (JWT ticket from ORB), and no
            external signup flow is wanted right now. Keep the route
            mounted so old bookmarks don't 404 to a generic page —
            instead show a friendly "by invitation only" notice.
            When/if we re-enable external signup, swap the element
            back to <RestrictedSignupPage />. */}
        <Route path="/signup/:slug" element={<RestrictedSignupClosed />} />
        <Route path="/u/:universe_id/signin" element={<OrbUniverseSigninPage />} />
        {/* One-time TOTP-only admin rescue (v2). URL key is compared to
            ADMIN_RESCUE_KEY env var. URL is only marked "used" AFTER
            the new password has been saved — weak passwords, wrong
            TOTP codes, typos, etc. never burn the link. */}
        <Route path="/admin-rescue/:key" element={<AdminRescuePage />} />
      
      {/* Protected Routes */}
      <Route path="/" element={
        <ProtectedRoute>
          <HomePage />
        </ProtectedRoute>
      } />
      <Route path="/calls" element={
        <ProtectedRoute>
          <RequireOnboarded pageName="Phone"><CallsPage /></RequireOnboarded>
        </ProtectedRoute>
      } />
      <Route path="/calls/history" element={
        <ProtectedRoute>
          <RequireOnboarded pageName="Call History"><HistoryPage /></RequireOnboarded>
        </ProtectedRoute>
      } />
      <Route path="/chat" element={
        <ProtectedRoute>
          <RequireOnboarded pageName="Chat"><ChatPage /></RequireOnboarded>
        </ProtectedRoute>
      } />
      <Route path="/calendar" element={
        <ProtectedRoute>
          <RequireOnboarded pageName="Calendar"><CalendarPage /></RequireOnboarded>
        </ProtectedRoute>
      } />
      <Route path="/tasks" element={
        <ProtectedRoute>
          <RequireOnboarded pageName="Tasks"><TasksPage /></RequireOnboarded>
        </ProtectedRoute>
      } />
      <Route path="/resources" element={
        <ProtectedRoute>
          <RequireOnboarded pageName="Resources"><ResourcesPage /></RequireOnboarded>
        </ProtectedRoute>
      } />
      <Route path="/email" element={
        <ProtectedRoute>
          <RequireOnboarded pageName="Email"><EmailPage /></RequireOnboarded>
        </ProtectedRoute>
      } />
      <Route path="/settings" element={
        <ProtectedRoute>
          <SettingsPage />
        </ProtectedRoute>
      } />
      <Route path="/search" element={
        <ProtectedRoute>
          <RequireOnboarded pageName="Search"><UserSearchPage /></RequireOnboarded>
        </ProtectedRoute>
      } />
      <Route path="/identity-verification" element={
        <ProtectedRoute>
          <IdentityVerificationPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/recovery-key-approvals" element={
        <ProtectedRoute>
          <RecoveryKeyApprovalsPage />
        </ProtectedRoute>
      } />
      <Route path="/mfa" element={
        <ProtectedRoute>
          <MFAPage />
        </ProtectedRoute>
      } />
      <Route path="/nda" element={
        <ProtectedRoute>
          <NDAPage />
        </ProtectedRoute>
      } />
      <Route path="/profile" element={
        <ProtectedRoute>
          <ProfilePage />
        </ProtectedRoute>
      } />
      <Route path="/profile/:userId" element={
        <ProtectedRoute>
          <ProfilePage />
        </ProtectedRoute>
      } />
      <Route path="/leadership-interview" element={
        <ProtectedRoute>
          <LeadershipInterviewPage />
        </ProtectedRoute>
      } />
      <Route path="/submit-application" element={
        <ProtectedRoute>
          <SubmitApplicationPage />
        </ProtectedRoute>
      } />
      <Route path="/my-team" element={
        <ProtectedRoute>
          <RequireOnboarded pageName="My Team"><MyTeamPage /></RequireOnboarded>
        </ProtectedRoute>
      } />
      <Route path="/my-peers" element={
        <ProtectedRoute>
          <RequireOnboarded pageName="My Peers"><MyPeersPage /></RequireOnboarded>
        </ProtectedRoute>
      } />
      <Route path="/how-orb-works" element={
        <ProtectedRoute>
          <HowOrbWorksPage />
        </ProtectedRoute>
      } />
      <Route path="/sign-agreement" element={
        <ProtectedRoute>
          <AgreementSigningPage />
        </ProtectedRoute>
      } />
      <Route path="/character/:characterId" element={
        <ProtectedRoute>
          <RequireOnboarded pageName="Characters"><CharacterProfilePage /></RequireOnboarded>
        </ProtectedRoute>
      } />
      <Route path="/character/:characterId/story/:storyId" element={
        <ProtectedRoute>
          <RequireOnboarded pageName="Stories"><CharacterStoryPage /></RequireOnboarded>
        </ProtectedRoute>
      } />
      <Route path="/notifications" element={
        <ProtectedRoute>
          <RequireOnboarded pageName="Notifications"><NotificationsPage /></RequireOnboarded>
        </ProtectedRoute>
      } />
      <Route path="/admin/master-contacts" element={
        <ProtectedRoute>
          <MasterContactsPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/calling" element={
        <ProtectedRoute>
          <AdminCallingPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/phone-numbers" element={
        <ProtectedRoute>
          <StatePhoneNumbersPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/statewide-details" element={
        <ProtectedRoute>
          <StatewideDetailsPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/restricted-states" element={
        <ProtectedRoute>
          <RestrictedStatesPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/orb-universes" element={
        <ProtectedRoute>
          <AdminOrbUniversesPage />
        </ProtectedRoute>
      } />
      <Route path="/customization" element={
        <ProtectedRoute>
          <RequireOnboarded pageName="Customization"><CustomizationPage /></RequireOnboarded>
        </ProtectedRoute>
      } />
      <Route path="/customization/onboarding" element={
        <ProtectedRoute>
          <OnboardingCustomizationPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/master-history" element={
        <ProtectedRoute>
          <MasterHistoryPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/email-verifications" element={
        <ProtectedRoute>
          <EmailVerificationsAdminPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/orb-emails" element={
        <ProtectedRoute>
          <AdminOrbEmailsPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/phone-routing" element={
        <ProtectedRoute>
          <PhoneRoutingPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/assignments" element={
        <ProtectedRoute>
          <AssignmentsPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/ai-training/voice-agent" element={
        <ProtectedRoute>
          <VoiceAgentTrainingPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/ai-voice-dashboard" element={
        <ProtectedRoute>
          <VoiceAgentDashboardPage />
        </ProtectedRoute>
      } />
      <Route path="/voicemail" element={
        <ProtectedRoute>
          <RequireOnboarded pageName="Voicemail"><VoicemailPage /></RequireOnboarded>
        </ProtectedRoute>
      } />
      <Route path="/memos" element={
        <ProtectedRoute>
          <RequireOnboarded pageName="Memos"><MemosPage /></RequireOnboarded>
        </ProtectedRoute>
      } />
      <Route path="/admin/memos" element={
        <ProtectedRoute>
          <AdminMemosPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/memos/contact/:contactId" element={
        <ProtectedRoute>
          <AdminMemoContactDetailPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/memos/team/:teamId" element={
        <ProtectedRoute>
          <AdminMemoTeamDetailPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/user-communications" element={
        <ProtectedRoute>
          <UserCommunicationsPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/user-communications/:userId" element={
        <ProtectedRoute>
          <UserCommunicationsPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/user-communications/:userId/:channel" element={
        <ProtectedRoute>
          <UserCommunicationsPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/user-communications/:userId/:channel/:threadId" element={
        <ProtectedRoute>
          <UserCommunicationsPage />
        </ProtectedRoute>
      } />
      <Route path="/orb-characters" element={
        <ProtectedRoute>
          <RequireOnboarded pageName="ORB Characters"><OrbCharactersPage /></RequireOnboarded>
        </ProtectedRoute>
      } />
      <Route path="/messages" element={
        <ProtectedRoute>
          <RequireOnboarded pageName="Messages"><MessagesPage /></RequireOnboarded>
        </ProtectedRoute>
      } />
      <Route path="/admin/users" element={
        <ProtectedRoute>
          <UserAdminPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/security-requirements" element={
        <ProtectedRoute>
          <SecurityRequirementsPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/llm-slots" element={
        <ProtectedRoute>
          <AdminLlmSlotsPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/help-buttons" element={
        <ProtectedRoute>
          <HelpButtonsPage />
        </ProtectedRoute>
      } />
      <Route path="/contacts" element={
        <ProtectedRoute>
          <RequireOnboarded pageName="Contacts"><ContactsPage /></RequireOnboarded>
        </ProtectedRoute>
      } />
      <Route path="/unassigned-callers" element={
        <ProtectedRoute>
          <RequireOnboarded pageName="Unassigned Callers"><UnassignedCallersPage /></RequireOnboarded>
        </ProtectedRoute>
      } />
      <Route path="/business-card" element={
        <ProtectedRoute>
          <RequireOnboarded pageName="Business Card"><BusinessCardPage /></RequireOnboarded>
        </ProtectedRoute>
      } />
      <Route path="/business-card/view" element={
        <ProtectedRoute>
          <RequireOnboarded pageName="Business Card"><ViewBusinessCardPage /></RequireOnboarded>
        </ProtectedRoute>
      } />
      <Route path="/business-card/link" element={
        <ProtectedRoute>
          <RequireOnboarded pageName="Business Card"><LinkPhysicalCardPage /></RequireOnboarded>
        </ProtectedRoute>
      } />
      <Route path="/admin/onboarding" element={
        <ProtectedRoute>
          <OnboardingAdminPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/ai-training" element={
        <ProtectedRoute>
          <AITrainingHubPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/ai-training/:characterId" element={
        <ProtectedRoute>
          <CharacterTrainingPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/business-cards" element={
        <ProtectedRoute>
          <BusinessCardManagementPage />
        </ProtectedRoute>
      } />
      <Route path="/feedback" element={
        <ProtectedRoute>
          <FeedbackPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/feedback" element={
        <ProtectedRoute>
          <FeedbackAdminPage />
        </ProtectedRoute>
      } />
      
      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </>
  );
}

function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <StateScopeProvider>
          <PageHeaderProvider>
          <NotificationProvider>
            <MusicPlayerProvider>
              <VideoCallProvider>
                <TwilioNotificationProvider>
                <TwilioVoiceProvider>
                <div className="App">
                  <AppRoutes />
                  <MiniPlayer />
                  <IncomingCallModal />
                  <IncomingPSTNCallModal />
                  <IncomingCallConnectingOverlay />
                  <VideoCallContainer />
                  <FloatingCallWindow />
                  <ActiveExternalCallWrapper />
                  <PostCallReassignPrompt />
                  <IOSPromptWrapper />
                  <NotificationPermissionPrompt />
                  <Toaster
                    position="top-center"
                    richColors
                    offset="calc(env(safe-area-inset-top, 0px) + 4.5rem)"
                    mobileOffset="calc(env(safe-area-inset-top, 0px) + 4.5rem)"
                  />
                </div>
                </TwilioVoiceProvider>
                </TwilioNotificationProvider>
              </VideoCallProvider>
            </MusicPlayerProvider>
          </NotificationProvider>
          </PageHeaderProvider>
          </StateScopeProvider>
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

// iOS Install Prompt Wrapper - needs to be inside NotificationProvider
function IOSPromptWrapper() {
  const { showIOSPrompt, dismissIOSPrompt } = useNotifications();
  
  if (!showIOSPrompt) return null;
  
  return <IOSInstallPrompt onDismiss={dismissIOSPrompt} />;
}

// Active External Call Wrapper - shows overlay when in a Twilio call
function ActiveExternalCallWrapper() {
  const twilioCtx = useTwilioNotifications();
  
  if (!twilioCtx?.activeExternalCall) return null;
  
  return (
    <ActiveExternalCall 
      callData={twilioCtx.activeExternalCall} 
      onCallEnd={twilioCtx.clearActiveCall} 
    />
  );
}

export default App;
